import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PropTypeBn } from '@blockcatio/tabby-library/lib/utilities/PropTypeBn';
import FormattedTokenWei from '@blockcatio/tabby-library/lib/components/FormattedTokenWei';

import NavigateAwayPrompt from '../../components/NavigateAwayPrompt';
import Payment from './Payment';
import { authorizeTokens } from '../../actions/token';
import { createCampaign } from '../../actions/campaigns';
import { authorizeCat } from '../../actions/account';
import { getCampaignDetails } from '../../selectors/createCampaign';
import { getTokenListByName } from '../../selectors/token';
import PropTypeCampaign from '../../utilities/propTypes/PropTypeCampaign';
import weiToFiat from '../../utilities/weiToFiat';

const mapStateToProps = (state) => {
  const { currencyCode } = state.currencyExchangeRates;
  return {
    campaignDetails: getCampaignDetails(state),
    ethBalance: state.account.balance,
    catBalance: state.account.catBalance,
    catAllowance: state.account.catAllowance,
    paymentFeeEth: state.app.ethFee,
    paymentFeeCat: state.app.catFee,
    tokenList: getTokenListByName(state),
    abandonCampaignCreationMessage: state.app.abandonCampaignCreationMessage,
    address: state.account.address,
    catExchangeRate: state.currencyExchangeRates.catExchangeRates[currencyCode] || 0,
    ethExchangeRate: state.currencyExchangeRates.ethExchangeRates[currencyCode] || 0,
    currencyCode,
    tokenAllowance: state.token.allowance,
  };
};

const mapDispatchToProps = dispatch => ({
  onAuthorizeTokens: (...args) => dispatch(authorizeTokens(...args)),
  onSubmit: (...args) => dispatch(createCampaign(...args)),
  onAuthorizeCat: (...args) => dispatch(authorizeCat(...args)),
});

export class PaymentContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isSending: false,
    };
    this.authorizeToken = this.authorizeToken.bind(this);
    this.canPayFee = this.canPayFee.bind(this);
    this.authorizeCatPayment = this.authorizeCatPayment.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  async authorizeToken() {
    const {
      campaignDetails: { tokenAddress, totalTokens },
      onAuthorizeTokens,
      tokenList,
      tokenAllowance,
    } = this.props;
    // Add to the allowance - need to preserve allowances for ongoing campaigns.
    const newAllowance = tokenAllowance.add(totalTokens);
    let authorizationMessage = (
      <p>
        This transaction authorizes our smart contract to transfer up to{' '}
        <strong>
          <FormattedTokenWei
            amount={newAllowance}
            tokenList={tokenList}
            tokenAddress={tokenAddress}
          />
        </strong>{' '}
        from your wallet when users redeem their reward.
      </p>
    );
    // Output an explanation as to why the allowance is > than current total.
    if (newAllowance.gt(totalTokens)) {
      authorizationMessage = (
        <React.Fragment>
          {authorizationMessage}
          <p className="note">
            <strong>Note:</strong> The authorization amount is the sum of tokens from your new
            campaign and the unclaimed tokens from your prior/ongoing campaigns (<FormattedTokenWei
              amount={totalTokens}
              tokenList={tokenList}
              tokenAddress={tokenAddress}
              showSymbol={false}
            />{' '}
            + unclaimed ={' '}
            <FormattedTokenWei
              amount={newAllowance}
              tokenList={tokenList}
              tokenAddress={tokenAddress}
            />).
          </p>
        </React.Fragment>
      );
    }

    const transactionHash = await onAuthorizeTokens(
      newAllowance,
      tokenAddress,
      authorizationMessage,
    );
    // No hash means early failure or user cancelled.
    return !!transactionHash;
  }

  canPayFee(paymentType) {
    const {
      paymentFeeEth, paymentFeeCat, ethBalance, catBalance,
    } = this.props;

    // No balances or fees yet.
    if (!ethBalance || !catBalance || !paymentFeeEth || !paymentFeeCat) {
      return false;
    }

    // Check if we have enough to pay for fees.
    if (paymentType === 'CAT' && catBalance.gte(paymentFeeCat)) {
      return true;
    } else if (paymentType === 'ETH' && ethBalance.gte(paymentFeeEth)) {
      return true;
    }
    return false;
  }

  async authorizeCatPayment() {
    const { paymentFeeCat, catAllowance, onAuthorizeCat } = this.props;
    // Already authorized - allowance >= amount.
    if (catAllowance.gte(paymentFeeCat)) {
      return true;
    }

    const transactionHash = await onAuthorizeCat(paymentFeeCat);
    // No hash means early failure or user cancelled.
    return !!transactionHash;
  }

  async handleClick(paymentType) {
    const { completed, onSubmit, onComplete } = this.props;

    if (!completed) {
      this.setState({ isSending: true });

      // Note: we are trusting the wallet will have enough balance for the authorized tokens.
      const authorized = await this.authorizeToken();
      // TODO: show error message if can't pay fee?
      if (authorized && this.canPayFee(paymentType)) {
        const canPay = paymentType === 'CAT' ? await this.authorizeCatPayment() : true;
        if (canPay) {
          if (await onSubmit(paymentType)) {
            return onComplete();
          }
        }
      }
      // Only update state on failure - onComplete loads a new component.
      this.setState({ isSending: false });
    }
    return false;
  }

  render() {
    const {
      campaignDetails,
      paymentFeeEth,
      paymentFeeCat,
      abandonCampaignCreationMessage,
      ethBalance,
      catBalance,
      address,
      currencyCode,
      catExchangeRate,
      ethExchangeRate,
    } = this.props;
    const fiatPaymentFeeEth = weiToFiat(
      paymentFeeEth,
      currencyCode,
      ethExchangeRate,
      currencyCode.toUpperCase(),
    );
    const fiatPaymentFeeCat = weiToFiat(
      paymentFeeCat,
      currencyCode,
      catExchangeRate,
      currencyCode.toUpperCase(),
    );
    return (
      <React.Fragment>
        <NavigateAwayPrompt
          key="NavigateAway"
          when={campaignDetails.transactionHash === ''}
          message={abandonCampaignCreationMessage}
        />
        <Payment
          key="Payment"
          campaignDetails={campaignDetails}
          paymentFeeEth={paymentFeeEth}
          paymentFeeCat={paymentFeeCat}
          isSending={this.state.isSending}
          onClick={this.handleClick}
          address={address}
          balance={ethBalance}
          catBalance={catBalance}
          fiatPaymentFeeEth={fiatPaymentFeeEth}
          fiatPaymentFeeCat={fiatPaymentFeeCat}
        />
      </React.Fragment>
    );
  }
}

PaymentContainer.displayName = 'PaymentContainer';

PaymentContainer.defaultProps = {
  campaignDetails: {},
  ethBalance: null,
  catBalance: null,
  paymentFeeEth: null,
  paymentFeeCat: null,
  abandonCampaignCreationMessage: '',
  address: '',
  catExchangeRate: 0,
  ethExchangeRate: 0,
  currencyCode: 'usd',
  tokenAllowance: null,
};

PaymentContainer.propTypes = {
  campaignDetails: PropTypes.shape(PropTypeCampaign),
  ethBalance: PropTypeBn,
  catBalance: PropTypeBn,
  paymentFeeEth: PropTypeBn,
  paymentFeeCat: PropTypeBn,
  abandonCampaignCreationMessage: PropTypes.string,
  address: PropTypes.string,
  catExchangeRate: PropTypes.number,
  ethExchangeRate: PropTypes.number,
  currencyCode: PropTypes.string,
  tokenAllowance: PropTypeBn,
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentContainer);



// WEBPACK FOOTER //
// ./src/containers/createCampaign/PaymentContainer.js